import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { TOURS } from "../tours";

const PAD = 8;

// TOUR — spotlight + card over the real screen. Starts when the URL has ?tour=<key>. Skips steps whose element isn't present.
export function Tour() {
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const key = params.get("tour");
  const tour = key ? TOURS[key] : null;
  const [steps, setSteps] = useState(null);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  // Resolve which steps have a live element (poll briefly for async content)
  useEffect(() => {
    if (!tour) { setSteps(null); return; }
    let tries = 0, alive = true;
    const resolve = () => {
      const found = tour.steps.filter((s) => document.querySelector(s.sel));
      if (found.length >= tour.steps.length || tries > 12) { if (alive) { setSteps(found); setI(0); } return; }
      tries += 1; setTimeout(resolve, 350);
    };
    resolve();
    return () => { alive = false; };
  }, [tour, loc.pathname]);

  const step = steps?.[i];
  useLayoutEffect(() => {
    if (!step) { setRect(null); return; }
    const el = document.querySelector(step.sel);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const measure = () => { const r = el.getBoundingClientRect(); setRect({ x: r.left - PAD, y: r.top - PAD, w: r.width + PAD * 2, h: r.height + PAD * 2 }); };
    const t = setTimeout(measure, 320); measure();
    window.addEventListener("resize", measure); window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [step]);

  const end = () => { params.delete("tour"); setParams(params, { replace: true }); };
  useEffect(() => { if (!tour) return; const onKey = (e) => { if (e.key === "Escape") end(); if (e.key === "ArrowRight") setI((n) => Math.min(n + 1, (steps?.length || 1) - 1)); if (e.key === "ArrowLeft") setI((n) => Math.max(n - 1, 0)); }; window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tour || steps === null) return null;
  if (steps.length === 0) return (
    <div className="fixed inset-x-0 bottom-6 z-[90] flex justify-center px-4" data-testid="tour-empty">
      <div className="card flex items-center gap-3 px-4 py-3 text-[13px] shadow-xl"><span className="t1">Nothing to show for “{tour.title}” on this screen yet — add some data and try again.</span><button className="btn-secondary btn-sm" onClick={end} data-testid="tour-close">Close</button></div>
    </div>
  );
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(360, vw - 24);
  const below = rect ? rect.y + rect.h + 12 + 170 < vh : true;
  const top = rect ? (below ? rect.y + rect.h + 12 : Math.max(12, rect.y - 12 - 170)) : vh / 2 - 90;
  const left = rect ? Math.min(Math.max(12, rect.x), vw - cardW - 12) : (vw - cardW) / 2;
  return (
    <div className="fixed inset-0 z-[90]" data-testid="tour">
      <div className="absolute inset-0" onClick={end} style={{ background: "transparent" }} />
      {rect && <div className="tour-spot" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} data-testid="tour-spotlight" />}
      <div className="tour-card animate-in" style={{ top, left, width: cardW }} data-testid="tour-card" role="dialog" aria-label={step.title}>
        <div className="row mb-1"><span className="eyebrow !mb-0">{tour.title} · {i + 1} of {steps.length}</span><button className="btn-icon !h-7 !w-7" onClick={end} aria-label="Close tour" data-testid="tour-close"><X size={14} /></button></div>
        <div className="t1 text-[15px] font-semibold">{step.title}</div>
        <p className="t3 mt-1 text-[13px] leading-relaxed">{step.body}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex gap-1">{steps.map((_, n) => <span key={n} className={`h-1.5 rounded-full transition-all ${n === i ? "w-4 bg-amber-500" : "w-1.5 bg-line"}`} />)}</div>
          <div className="flex gap-2">
            {i > 0 && <button className="btn-secondary btn-sm" onClick={() => setI(i - 1)} data-testid="tour-back"><ArrowLeft size={13} /> Back</button>}
            {i < steps.length - 1 ? <button className="btn-primary btn-sm" onClick={() => setI(i + 1)} data-testid="tour-next">Next <ArrowRight size={13} /></button>
              : <button className="btn-primary btn-sm" onClick={end} data-testid="tour-done">Done</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
